import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import {
  ShamBuildings03Icon,
  ShamLocationAddressIcon,
  ShamPhone03Icon,
  ShamUserIcon,
} from 'sham-components/icons';
import { ShamGeoApi } from 'sham-components/services/sham-geo-api';
import { City, Country } from 'sham-components/shared/models/auth';
import { Address, AddressService } from '../../services/address.service';
import {
  LocationPicker,
  MapViewport,
  SelectedLocation,
} from '../../elements/location-picker/location-picker';

@Component({
  selector: 'app-shipping-address-form',
  imports: [
    FormsModule,
    TranslateModule,
    ShamUserIcon,
    ShamBuildings03Icon,
    ShamLocationAddressIcon,
    ShamPhone03Icon,
    LocationPicker,
  ],
  templateUrl: './shipping-address-form.html',
  styleUrl: './shipping-address-form.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShippingAddressFormComponent implements OnInit {
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private readonly phonePattern = /^[0-9+\s()-]{7,20}$/;
  private cityRequestId = 0;
  private mapCenterRequestId = 0;
  private reverseGeocodeRequestId = 0;

  private readonly addressService = inject(AddressService);
  private readonly geoApi = inject(ShamGeoApi);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  readonly initialData = input<Address | null>(null);
  readonly onCancel = output<void>();
  readonly onSave = output<void>();
  private readonly navigationAddress: Address | null;

  isLoading = signal<boolean>(false);
  isSaving = false;
  errorMessage = '';
  submitted = false;

  countries: Country[] = [];
  cities: City[] = [];
  mapViewport: MapViewport | null = null;

  addressForm = {
    fullName: '',
    email: '',
    company: null as string | null,
    streetAddress: '',
    countryId: '',
    cityId: '',
    phone: '',
    isDefault: true,
    latitude: null as number | null,
    longitude: null as number | null,
    isPinConfirmed: false,
  };

  constructor() {
    const navigation = this.router.getCurrentNavigation();
    this.navigationAddress = navigation?.extras.state?.['address'] ?? null;
  }

  ngOnInit() {
    this.isLoading.set(true);

    this.geoApi.getCountries().subscribe({
      next: (data: Country[]) => {
        this.countries = data;
        const existingAddress = this.existingAddress();
        if (existingAddress)
          this.loadCities(existingAddress.countryId, existingAddress.cityId);
        this.isLoading.set(false);
      },
      error: (err: unknown) => {
        console.error('Error fetching countries', err);
        this.isLoading.set(false);
      },
    });

    const existingAddress = this.existingAddress();
    if (existingAddress) {
      this.addressForm = {
        fullName: existingAddress.fullName,
        email: existingAddress.email,
        company: existingAddress.company,
        streetAddress: existingAddress.streetAddress,
        countryId: existingAddress.countryId,
        cityId: existingAddress.cityId,
        phone: existingAddress.phone,
        isDefault: existingAddress.isDefault,
        latitude: existingAddress.latitude,
        longitude: existingAddress.longitude,
        isPinConfirmed: existingAddress.isPinConfirmed,
      };
    }
  }

  onLocationChange(location: SelectedLocation) {
    this.addressForm.latitude = location.latitude;
    this.addressForm.longitude = location.longitude;
    this.addressForm.isPinConfirmed = location.isPinConfirmed;
    if (location.isPinConfirmed) {
      this.errorMessage = '';
      return;
    }

    const requestId = ++this.reverseGeocodeRequestId;
    this.addressService
      .reverseGeocode(
        location.latitude,
        location.longitude,
        this.translate.currentLang || 'en',
      )
      .subscribe({
        next: (result) => {
          if (requestId !== this.reverseGeocodeRequestId) return;

          if (result.streetAddress.trim()) {
            this.addressForm.streetAddress = result.streetAddress;
          }
          if (result.countryId) {
            this.addressForm.countryId = result.countryId;
            this.loadCities(result.countryId, result.cityId ?? '');
          }
          this.cdr.markForCheck();
        },
        error: (error) => console.error('Reverse geocoding failed', error),
      });
  }

  showLocationError(): boolean {
    return this.submitted && !this.isLocationValid();
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  isFullNameValid(): boolean {
    return this.addressForm.fullName.trim().length > 0;
  }

  isStreetAddressValid(): boolean {
    return this.addressForm.streetAddress.trim().length > 0;
  }

  isCountryValid(): boolean {
    return this.addressForm.countryId.trim().length > 0;
  }

  isCityValid(): boolean {
    return this.addressForm.cityId.trim().length > 0;
  }

  isEmailValid(): boolean {
    return this.emailPattern.test(this.addressForm.email.trim());
  }

  isPhoneValid(): boolean {
    return this.phonePattern.test(this.addressForm.phone.trim());
  }

  getFullNameErrorMessage(): string {
    return this.t('addressValidation.fullNameRequired');
  }

  getStreetAddressErrorMessage(): string {
    return this.t('addressValidation.streetAddressRequired');
  }

  getCountryErrorMessage(): string {
    return this.t('addressValidation.countryRequired');
  }

  getCityErrorMessage(): string {
    return this.t('addressValidation.cityRequired');
  }

  getEmailErrorMessage(): string {
    if (!this.addressForm.email.trim()) {
      return this.t('addressValidation.emailRequired');
    }

    return this.t('addressValidation.emailInvalid');
  }

  getPhoneErrorMessage(): string {
    if (!this.addressForm.phone.trim()) {
      return this.t('addressValidation.phoneRequired');
    }

    return this.t('addressValidation.phoneInvalid');
  }

  showFullNameError(): boolean {
    return !this.isFullNameValid() && this.submitted;
  }

  showStreetAddressError(): boolean {
    return !this.isStreetAddressValid() && this.submitted;
  }

  showCountryError(): boolean {
    return !this.isCountryValid() && this.submitted;
  }

  showCityError(): boolean {
    return !this.isCityValid() && this.submitted;
  }

  showEmailError(): boolean {
    return this.submitted && (!this.addressForm.email.trim() || !this.isEmailValid());
  }

  showPhoneError(): boolean {
    return this.submitted && (!this.addressForm.phone.trim() || !this.isPhoneValid());
  }

  onCountryChange() {
    this.reverseGeocodeRequestId++;
    this.addressForm.cityId = '';
    this.clearPin();

    if (!this.addressForm.countryId) {
      this.cityRequestId++;
      this.cities = [];
      this.mapViewport = null;
      return;
    }

    this.loadCities(this.addressForm.countryId);
    this.centerMap(this.addressForm.countryId, null);
  }

  onCityChange() {
    this.reverseGeocodeRequestId++;
    this.clearPin();

    if (this.addressForm.countryId && this.addressForm.cityId) {
      this.centerMap(this.addressForm.countryId, this.addressForm.cityId);
    }
  }

  private loadCities(countryId: string, selectedCityId = '') {
    const requestId = ++this.cityRequestId;
    this.cities = [];
    this.addressForm.cityId = selectedCityId;

    this.geoApi.getCities(countryId).subscribe({
      next: (data: City[]) => {
        if (requestId !== this.cityRequestId) return;
        this.cities = data;
        this.addressForm.cityId = data.some((city) => city.id === selectedCityId)
          ? selectedCityId
          : '';
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestId !== this.cityRequestId) return;
        this.cities = [];
        this.addressForm.cityId = '';
        this.cdr.markForCheck();
      },
    });
  }

  private centerMap(countryId: string, cityId: string | null) {
    const requestId = ++this.mapCenterRequestId;
    this.addressService
      .getMapCenter(countryId, cityId, this.translate.currentLang || 'en')
      .subscribe({
        next: (viewport) => {
          if (requestId !== this.mapCenterRequestId) return;
          this.mapViewport = viewport;
          this.cdr.markForCheck();
        },
        error: (error) => console.error('Map geocoding failed', error),
      });
  }

  private clearPin() {
    this.addressForm.latitude = null;
    this.addressForm.longitude = null;
    this.addressForm.isPinConfirmed = false;
  }

  private getValidationErrorMessage(): string {
    if (!this.isFullNameValid()) {
      return this.getFullNameErrorMessage();
    }

    if (!this.isStreetAddressValid()) {
      return this.getStreetAddressErrorMessage();
    }

    if (!this.isCountryValid()) {
      return this.getCountryErrorMessage();
    }

    if (!this.isCityValid()) {
      return this.getCityErrorMessage();
    }

    if (!this.addressForm.email.trim()) {
      return this.t('addressValidation.emailRequired');
    }

    if (!this.isEmailValid()) {
      return this.t('addressValidation.emailInvalid');
    }

    if (!this.addressForm.phone.trim()) {
      return this.t('addressValidation.phoneRequired');
    }

    if (!this.isPhoneValid()) {
      return this.t('addressValidation.phoneInvalid');
    }

    if (!this.isLocationValid()) {
      return this.t('addressValidation.locationRequired');
    }

    return this.t('addressValidation.formInvalid');
  }

  private isFormValid(): boolean {
    return (
      this.isFullNameValid() &&
      this.isStreetAddressValid() &&
      this.isCountryValid() &&
      this.isCityValid() &&
      this.addressForm.email.trim().length > 0 &&
      this.isEmailValid() &&
      this.addressForm.phone.trim().length > 0 &&
      this.isPhoneValid() &&
      this.isLocationValid()
    );
  }

  private isLocationValid(): boolean {
    return (
      this.addressForm.latitude !== null &&
      this.addressForm.longitude !== null &&
      this.addressForm.isPinConfirmed
    );
  }

  private buildRequestBody() {
    return {
      ...this.addressForm,
      fullName: this.addressForm.fullName.trim(),
      email: this.addressForm.email.trim(),
      streetAddress: this.addressForm.streetAddress.trim(),
      phone: this.addressForm.phone.trim(),
      latitude: this.addressForm.latitude!,
      longitude: this.addressForm.longitude!,
      isPinConfirmed: this.addressForm.isPinConfirmed,
    };
  }

  saveAddress() {
    this.submitted = true;
    this.errorMessage = '';

    if (!this.isFormValid()) {
      this.errorMessage = this.getValidationErrorMessage();
      return;
    }

    this.isSaving = true;

    const requestBody = this.buildRequestBody();
    const existingAddress = this.existingAddress();
    const obs: Observable<unknown> = existingAddress
      ? this.addressService.updateAddress(existingAddress.id, requestBody)
      : this.addressService.addAddress(requestBody);

    obs.subscribe({
      next: () => {
        this.isSaving = false;
        this.cdr.markForCheck();
        this.onSave.emit();
        this.onCancel.emit();
        history.back();
      },
      error: (err: unknown) => {
        this.isSaving = false;
        this.errorMessage = this.getApiErrorMessage(err);
        this.cdr.markForCheck();
      },
    });
  }

  goBack() {
    history.back();
  }

  private existingAddress(): Address | null {
    return this.initialData() ?? this.navigationAddress;
  }

  private getApiErrorMessage(error: unknown): string {
    if (typeof error !== 'object' || error === null) {
      return this.t('addressValidation.requestFailed');
    }
    const response = error as {
      error?: { error?: { message?: string }; message?: string };
    };
    return (
      response.error?.error?.message ??
      response.error?.message ??
      this.t('addressValidation.requestFailed')
    );
  }
}
